/*
 * @Description   : 计算首屏性能
 * @usage         : 这里是将 aegis 内部 plugin/page-performance.ts 的计算逻辑摘到这里来，与 html 收集的材料配合计算
 * @FilePath: /app-monorepo-projects/packages/app-share/src/libs/ageis/page-performance.ts
 */

import { IS_SSR } from '@/utils/env';
import type { PagePerformanceLog, IAegisPerfMaterial, IMarkDoms } from '@/types/aegis';

interface IFirstScreenInfo {
  element: Element;
  timing: number;
  markDoms?: IMarkDoms;
}

let timeout;
// 重试次数
let retryTimes = 1;
// 等待多少 ms 之后开始计算首屏性能数据，源码是 3000ms，但到达这里说明正常 SSR 渲染下，DOM 已经出来了，等待时间可以相应缩短
const TIMEOUT = 2000;

/**
 * 计算首屏性能
 *
 * @export
 * @param {IAegisPerfMaterial | undefined} materialNS 挂载首屏材料的全局变量
 * @return {*}  {(Promise<IFirstScreenInfo | undefined>)}
 */
export async function getFirstScreenPerformance(materialNS: IAegisPerfMaterial | undefined):
Promise<PagePerformanceLog | undefined> {
  if (IS_SSR || !materialNS) return;

  // 如果不存在 performance.timing，直接返回
  const t = performance.timing;
  if (!t) return;

  // 如果首屏相关信息获取不到，直接返回
  const firstScreenInfo = await new Promise<IFirstScreenInfo | undefined>((resolve) => {
    timeout = setTimeout(() => {
      resolve(getFirstScreenInfo(materialNS));
    }, TIMEOUT);
  });
  if (!firstScreenInfo) return;

  // 计算首屏性能数据并返回
  const getResourceDownloadCost = (t: Performance['timing']) => {
    let resourceDownload = t.loadEventStart - t.domInteractive;
    // 这里的 1070 是 aegis 内部源码的值，这里保留
    if (resourceDownload < 0) resourceDownload = 1070;
    return resourceDownload;
  };
  const getDomParseCost = (t: Performance['timing']) => t.domInteractive - t.domLoading;
  const { timing: firstScreenTiming } = firstScreenInfo;
  console.log('[getFirstScreenPerformance] firstScreenInfo:', firstScreenInfo);
  return {
    dnsLookup: t.domainLookupEnd - t.domainLookupStart,
    tcp: t.connectEnd - t.connectStart,
    ssl: t.secureConnectionStart === 0 ? 0 : t.requestStart - t.secureConnectionStart,
    ttfb: t.responseStart - t.requestStart,
    contentDownload: t.responseEnd - t.responseStart,
    domParse: getDomParseCost(t),
    resourceDownload: getResourceDownloadCost(t),
    firstScreenTiming: Math.floor(firstScreenTiming),
  };
}

// 内部计算首屏性能
function getFirstScreenInfo(materialNS: IAegisPerfMaterial): Promise<IFirstScreenInfo | undefined> {
  return new Promise((resolve) => {
    let firstScreenTiming = 0;
    let maxChange = 0;
    let firstScreenInfo: IFirstScreenInfo | undefined;
    const {
      markDoms = {},
      changeList = [],
      observeDom,
    } = (materialNS || {}) as IAegisPerfMaterial;
    const markDomTimings: number[] = Object
      .keys(markDoms)
      .filter((timing: string) => markDoms[timing].find((ele: Element) => isInFirstScreen(ele)))
      .map(timing => Number(timing));

    if (markDomTimings.length) {
      // 用户手动标记的需要检查首屏的dom最晚出现的时间
      firstScreenTiming = Math.max.apply(null, markDomTimings);
      firstScreenInfo = {
        element: markDoms[firstScreenTiming]?.[0],
        timing: firstScreenTiming,
        markDoms,
      };
    } else {
      changeList.forEach((change) => {
        for (let i = 0; i < change.roots.length; i++) {
          if (change.rootsDomNum[i] > maxChange && isInFirstScreen(change.roots[i])) {
            maxChange = change.rootsDomNum[i];
            firstScreenTiming = change.time;
            firstScreenInfo = {
              element: change.roots[i],
              timing: firstScreenTiming,
            };
          }
        }
      });
    }
    retryTimes -= 1;
    if (!firstScreenTiming && retryTimes >= 0) {
      timeout = setTimeout(() => resolve(getFirstScreenInfo(materialNS)), TIMEOUT);
      console.log(timeout);
    } else {
      observeDom?.disconnect();
      resolve(firstScreenInfo);
    }
  });
};


// 判断是否在首屏范围内
function isInFirstScreen(target: Element) {
  if (!target || typeof target.getBoundingClientRect !== 'function') return false;
  const rect = target.getBoundingClientRect();
  const screenHeight = window.innerHeight;
  const screenWidth = window.innerWidth;
  return (
    rect.right > 0
    && rect.left < screenWidth
    && rect.bottom > 0
    && rect.top < screenHeight
    && rect.width > 0
    && rect.height > 0
  );
}
